import { reactive, readonly, ref } from 'vue'
import { isEqual } from 'lodash'

import events$ from '@/services/Events'
import petLeadsAPI from '@/api/petLeadsAPI'

import { logInfo } from '@/helpers/Logger'
import { ReactivityUtil } from '@/utils/Reactivity'

import sessionStore from '@/store/session'

import { EVENT_LEAD } from '@/config/events'

// DATA
const store = reactive({})
const lead = readonly(store)
const leadCodeStore = reactive({ value: '' })
const healthStateStore = reactive({ isRejected: false, hasHealthClearance: false })
const deductibleAdjustedStore = reactive({ isAdjusted: false })
const initialized = ref(false)

// EVENTS
events$.on(EVENT_LEAD.DEDUCTIBLE_ADJUSTED, () => {
  setDeductibleAdjusted()
})

events$.on(EVENT_LEAD.HEALTH_ANSWERS_CLEARED, () => {
  setHealthClearance()
})

// METHODS
function emptyLead() {
  ReactivityUtil.resetData(store)
  leadCodeStore.value = null
  healthStateStore.isRejected = false
  deductibleAdjustedStore.isAdjusted = false
  
  // Clear leadCode from session but preserve randomSessionId for analytics
  const currentSession = sessionStore.getSession()
  if (currentSession && currentSession.leadCode) {
    sessionStore.setSession({ leadCode: null }, { preventCookie: false, hot: currentSession.hot })
  }
}

/**
 * Set health clearence state
 */
function setHealthClearance() {
  healthStateStore.hasHealthClearance = true
}

/**
 * Reset health clearence state
 */
function resetHealthClearance() {
  healthStateStore.hasHealthClearance = false
}

/**
 * Get health clearence state
 * @returns {boolean} - Whether the pet health questions were cleared
 */
function hasHealthClearance() {
  return healthStateStore.hasHealthClearance
}

/**
 * Set health rejection state
 */
function setHealthRejection() {
  healthStateStore.isRejected = true
}

/**
 * Reset health rejection state
 */
function resetHealthRejection() {
  healthStateStore.isRejected = false
}

/**
 * Get health rejection state
 * @returns {boolean} - Whether the pet health is rejected
 */
function isHealthRejectedLead() {
  return healthStateStore.isRejected
}

/**
 * Set deductible adjusted state
 */
function setDeductibleAdjusted() {
  deductibleAdjustedStore.isAdjusted = true
}

/**
 * Reset deductible adjusted state
 */
function resetDeductibleAdjusted() {
  deductibleAdjustedStore.isAdjusted = false
}

/**
 * Get deductible adjusted state
 * @returns {boolean} - Whether the deductible has been adjusted
 */
function hasDeductibleAdjusted() {
  return deductibleAdjustedStore.isAdjusted
}

function leadStore() {
  // Initialize leadCode from session on startup
  function initializeFromSession() {
    try {
      const session = sessionStore.getSession()
      if (session?.leadCode) {
        setLeadCode(session.leadCode)
      }
    } catch (error) {
      // Ignore errors during initialization (e.g., appGroup not ready)
      console.warn('Failed to initialize from session:', error)
    }
  }

  /**
   * Wait for the leadStore to be initialized and optionally fetch lead data
   * @returns {Promise} - Resolves when store is ready
   */
  function ready() {
    return new Promise(function (resolve) {
      if (initialized.value) {
        resolve()
        return
      }

      // Try to initialize from session when ready() is called
      Promise.resolve().then(async () => {
        try {
          initializeFromSession()
          
          // If we have a leadCode but no lead data, fetch it
          const leadCode = getLeadCode()
          if (leadCode && (!lead || Object.keys(lead).length === 0)) {
            try {
              await getLead({ leadCode })
            } catch (error) {
              console.warn('Failed to fetch lead data:', error)
            }
          }
        } catch (error) {
          console.warn('Failed to initialize from session:', error)
        }
        initialized.value = true
        resolve()
      })
    })
  }

  // METHODS
  async function createLead(payload) {
    const partnerStore = (await import('@/store/partner')).default
    
    const completePayload = { ...payload }
    const payloadSalesPartnerNumber = partnerStore.getPayloadSalesPartnerNumber()
    if (payloadSalesPartnerNumber) {
      completePayload.salesPartnerNumber = payloadSalesPartnerNumber
    }

    const createdLead = await petLeadsAPI.createPetLead(completePayload)

    if (!createdLead) return

    if (createdLead.leadCode) {
      sessionStore.setSession(createdLead)
    }
    setLead(createdLead)

    return ReactivityUtil.clone(lead)
  }

  /**
   * Trys to fetch a lead from BE, either for the reentry or normal operation
   */
  async function getLead({ leadCode }, ignoreSession = false) {
    if (!leadCode) {
      throw new Error('Lead code is required to fetch lead data')
    }

    const response = await petLeadsAPI.getPetLeadByCode(leadCode)

    if (ignoreSession) {
      return ReactivityUtil.clone(response)
    }

    // Only set session and lead if response is valid
    if (response?.leadCode) {
      sessionStore.setSession(response)
      setLead(response)
    }

    return ReactivityUtil.clone(response)
  }

  function getLeadCode() {
    // Ensure initialization happens when leadCode is first accessed
    initializeFromSession()
    return leadCodeStore.value
  }

  function getPetName() {
    if (getLeadCode()) {
      return lead?.policyData?.petData?.name || ''
    }
    return ''
  }

  function setLeadCode(leadCode) {
    leadCodeStore.value = leadCode
  }

  function setLead(newLead) {
    const originalLead = ReactivityUtil.clone(lead)

    const { leadCode, ...leadDataWithoutCode } = newLead

    if (leadCode !== undefined) {
      setLeadCode(leadCode)
    }

    let madeChange = false
    for (const key in leadDataWithoutCode) {
      if (!isEqual(leadDataWithoutCode[key], store[key])) {
        store[key] = leadDataWithoutCode[key]
        madeChange = true
      }
    }

    if (madeChange) {
      logInfo(['%cLEAD SET', 'color: green', lead])
      events$.emit(EVENT_LEAD.UPDATED, {
        lead,
        originalLead,
      })
    }
  }

  /**
   * Update pet lead via API call
   * @param {Object} leadData - lead data to update
   */
  async function updatePetLead(leadData) {
    const leadCode = getLeadCode()
    const updatedLead = await petLeadsAPI.updatePetLead(leadCode, leadData)

    if (!updatedLead) return

    setLead(updatedLead)
    return ReactivityUtil.clone(lead)
  }

  /**
   * Submit pet lead via API call
   * @param {Object} leadData - lead data to submit (will be merged with existing lead data)
   */
  async function submitPetLead(leadData) {
    const leadCode = getLeadCode()
    const completeLeadData = { ...ReactivityUtil.clone(lead), ...leadData }

    const partnerStore = (await import('@/store/partner')).default
    
    const payloadSalesPartnerNumber = partnerStore.getPayloadSalesPartnerNumber()
    if (payloadSalesPartnerNumber) {
      completeLeadData.salesPartnerNumber = payloadSalesPartnerNumber
    }

    const updatedLead = await petLeadsAPI.submitPetLead(leadCode, completeLeadData)

    if (!updatedLead) return

    setLead(updatedLead)
    return ReactivityUtil.clone(lead)
  }

  /**
   * Submit a lead via the testing endpoint with an optional policy start date override.
   *
   * @param {Object} leadData - Lead data merged with existing lead data.
   * @param {string} overwritePolicyStartDate - Start date override in ISO yyyy-MM-dd format.
   * @returns {Promise<void>}
   */
  async function submitPetLeadOnlyForTesting(leadData, overwritePolicyStartDate) {
    const leadCode = getLeadCode()
    const completeLeadData = { ...ReactivityUtil.clone(lead), ...leadData }

    const partnerStore = (await import('@/store/partner')).default
    const payloadSalesPartnerNumber = partnerStore.getPayloadSalesPartnerNumber()
    if (payloadSalesPartnerNumber) {
      completeLeadData.salesPartnerNumber = payloadSalesPartnerNumber
    }

    await petLeadsAPI.submitPetLeadOnlyForTesting(leadCode, completeLeadData, overwritePolicyStartDate)
  }

  return {
    lead,
    leadCode: readonly(leadCodeStore),
    createLead,
    emptyLead,
    getLead,
    getLeadCode,
    getPetName,
    persistLead: setLead,
    submitPetLead,
    submitPetLeadOnlyForTesting,
    updatePetLead,
    setHealthClearance,
    resetHealthClearance,
    hasHealthClearance,
    resetHealthRejection,
    setHealthRejection,
    isHealthRejectedLead,
    setDeductibleAdjusted,
    resetDeductibleAdjusted,
    hasDeductibleAdjusted,
    ready,
  }
}

export default leadStore()
